import { useState } from "react";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { Info, RefreshCw, Receipt, ShieldCheck, Sparkles, Trophy, UsersRound } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { HeroCard, HeroPill, HeroWordmark, IconSquare, Panel, PanelTitle, Segmented, ShareBar, StatGrid, StateChip } from "@/components/panels";
import { creditUnit, formatCount, formatUsd, purchaseLabel } from "./alloyView";

// Every part that prints an amount takes `unit` (creditUnit): Alloy tokens for a brand on tokens, dollars otherwise.
const DOLLARS = creditUnit();

const TIER_STYLES = Object.freeze({
  premium: { bar: "bg-[#003B8D]", label: "premium" },
  standard: { bar: "bg-[#4690D9]", label: "standard" },
  fast: { bar: "bg-[#9cc6f0]", label: "fast" },
});

/** The ON/OFF (or auto-recharge) switch: a real button with switch semantics, sized for touch. */
export function Switch({ checked, disabled, onClick, label, tone = "dark", size = "md", ...rest }) {
  const dims = size === "sm" ? { track: "h-[22px] w-10", thumb: "h-[18px] w-[18px]", on: "translate-x-[18px]" } : { track: "h-7 w-12", thumb: "h-6 w-6", on: "translate-x-5" };
  const track = tone === "light"
    ? (checked ? "bg-white/90" : "bg-white/25")
    : (checked ? "bg-sm-primary" : "bg-sm-ink/20");
  const thumb = tone === "light" ? (checked ? "bg-sm-primary" : "bg-white") : "bg-white";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(checked)}
      aria-label={label}
      {...rest}
      disabled={disabled}
      onClick={onClick}
      className="group inline-flex min-h-[32px] shrink-0 items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className={`relative inline-flex ${dims.track} items-center rounded-full p-[2px] transition-colors ${track}`}>
        <span className={`inline-block ${dims.thumb} rounded-full shadow-[0_1px_3px_rgba(15,36,66,0.35)] transition-transform ${thumb} ${checked ? dims.on : "translate-x-0"}`} />
      </span>
    </button>
  );
}

export function StatusChip({ view }) {
  return <StateChip testId="alloy-status" tone={view.pending ? "busy" : view.on ? "ok" : "quiet"}>{view.label}</StateChip>;
}

/** The balance card: GrowthOS Alloy, the balance and its runway, the workspace and auto-recharge. */
// sharedWith: the host brand's name when this brand profile shares its host's billing. Its own balance is not what
// its agent spends, so the card names the host instead of a balance, a runway and an auto-recharge.
export function CreditsCard({ balanceUsd, balance = null, unit = DOLLARS, runway, view, workspaceName, autoRecharge, canManage, busy, onToggleAutoRecharge, sharedWith = null }) {
  const pill = view.pending ? { text: view.state === "turning-on" ? "STARTING" : "STOPPING", tone: "busy" } : view.on ? { text: "ON", tone: "on" } : { text: "OFF", tone: "quiet" };
  const packageUsd = Math.round((Number(autoRecharge?.packageCents) || 5000) / 100);
  const thresholdUsd = Number(autoRecharge?.thresholdUsd) || 20;
  const complimentary = autoRecharge?.funding === "complimentary";
  // The balance text: the tokens block's own figure when the page is on tokens, else the dollar balance.
  const balanceText = balance ?? formatUsd(balanceUsd);
  // Every top-up reads in Austin's words (2026-11-02): what it adds in dollars, and the threshold in tokens or dollars.
  // A paid top-up adds its package; a free refill adds refillCents. An older free refill that restores a target
  // balance (targetUsd, no refillCents) keeps its own line.
  const legacyTarget = complimentary && !(Number(autoRecharge?.refillCents) > 0);
  const topUpUsd = complimentary ? Math.round(Number(autoRecharge?.refillCents) / 100) : packageUsd;
  const below = unit.tokens ? `${unit.amount(thresholdUsd)} tokens` : formatUsd(thresholdUsd);
  const rechargeLine = !autoRecharge?.enabled
    ? "Off"
    : legacyTarget
      ? (unit.tokens ? `To ${unit.amount(autoRecharge.targetUsd)} when under ${below}` : `To $${autoRecharge.targetUsd} when under $${thresholdUsd}`)
      : `Tops up $${topUpUsd} whenever below ${below}`;
  return (
    <HeroCard data-testid="alloy-card">
      <div className="flex items-center justify-between gap-3">
        <HeroWordmark product="Alloy" />
        {sharedWith ? null : <HeroPill testId="alloy-card-state" tone={pill.tone} dot={view.on && !view.pending}>{pill.text}</HeroPill>}
      </div>
      {sharedWith ? (
        <div>
          <div className="text-xs font-medium text-white/70">Billing</div>
          <div data-testid="alloy-shared-host" className="mt-1.5 font-display text-[28px] font-extrabold leading-tight tracking-[-0.01em]">{`Shared with ${sharedWith}`}</div>
        </div>
      ) : (
        <div>
          <div className="text-xs font-medium text-white/70">Balance</div>
          <div data-testid="alloy-balance" className="mt-1.5 font-display text-[46px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">{balanceText}</div>
          {unit.tokens ? <div data-testid="alloy-unit" className="mt-1.5 text-[13px] font-bold uppercase tracking-[0.12em] text-white/80">{unit.name}</div> : null}
          {runway ? <div data-testid="alloy-runway" className="mt-2 text-[13px] text-white/75">{runway}</div> : null}
        </div>
      )}
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 truncate text-[13px] font-bold text-white/90">{workspaceName}</div>
        {sharedWith ? null : <div className="flex shrink-0 items-center gap-2.5">
          <div className="text-right">
            <div className="text-xs font-bold text-white/90">{complimentary ? "Free auto-refill" : "Auto-recharge"}</div>
            <div className="text-[11px] tabular-nums text-white/65">{rechargeLine}</div>
          </div>
          <Switch
            data-testid="auto-recharge-toggle"
            size="sm"
            tone="light"
            checked={Boolean(autoRecharge?.enabled)}
            disabled={busy || !canManage}
            label={complimentary ? "Free auto-refill" : "Auto-recharge"}
            onClick={onToggleAutoRecharge}
          />
        </div>}
      </div>
    </HeroCard>
  );
}

/** Reasoning effort as a segmented control, with the model line it applies to. */
export function EffortPanel({ model, options, optionLabel, canManage, busy, alloyOn, modelText, changeNote, onChoose, onResetModel, unit = DOLLARS }) {
  const current = model?.reasoningEffort || "medium";
  return (
    <Panel className="flex flex-col gap-2.5 !py-[18px]">
      <PanelTitle aside={<span data-testid="alloy-model" className="text-xs text-sm-ink/65">Model: {modelText}</span>}>Reasoning effort</PanelTitle>
      {canManage ? (
        <Segmented
          testId="alloy-effort"
          label="Reasoning effort"
          value={current}
          busy={busy}
          onChange={onChoose}
          options={options.map((value) => ({ value, label: optionLabel(value), testId: `alloy-effort-${value}` }))}
        />
      ) : (
        <p className="text-[13px] text-sm-ink/75"><strong className="text-sm-ink">{optionLabel(current)}</strong> for your whole agent.</p>
      )}
      <p className="text-xs leading-relaxed text-sm-ink/65">Higher effort thinks longer and spends {unit.noun} faster. Applies to your whole agent within about a minute.</p>
      {model?.overridden ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-sm-ink/10 pt-2.5 text-xs text-sm-ink/65">
          <span data-testid="alloy-model-change">{changeNote} Your team can change this any time by telling your agent in Slack.</span>
          {canManage ? (
            <button type="button" data-testid="alloy-model-reset" disabled={busy} onClick={onResetModel}
              className="rounded-lg border border-sm-ink/15 px-2 py-1 text-xs font-bold text-sm-ink hover:bg-sm-ink/5 disabled:cursor-not-allowed disabled:opacity-60">
              Back to auto
            </button>
          ) : null}
        </div>
      ) : null}
      {canManage && alloyOn ? (
        <p data-testid="alloy-model-locked" className="text-xs text-sm-ink/55">The model is set while Alloy is on. Turn Alloy off to choose a specific one.</p>
      ) : null}
    </Panel>
  );
}

/** What a package is called on a button: "20,000 tokens · $20" on tokens, "$20" otherwise. */
export function packageChoiceLabel(entry, unit = DOLLARS) {
  return unit.tokens ? `${unit.fromCents(entry.amountCents)} tokens · ${entry.label}` : entry.label;
}

export function AmountChoices({ packages, selectedCents, onSelect, disabled, unit = DOLLARS }) {
  return (
    <ToggleGroup.Root
      type="single"
      value={selectedCents ? String(selectedCents) : ""}
      onValueChange={(value) => { if (value) onSelect(Number(value)); }}
      disabled={disabled}
      aria-label={unit.tokens ? "Token amount" : "Credit amount"}
      data-testid="credits-amount-choices"
      className="grid grid-cols-3 gap-2.5"
    >
      {packages.map((entry) => (
        <ToggleGroup.Item
          key={entry.amountCents}
          value={String(entry.amountCents)}
          aria-label={unit.tokens ? `${unit.fromCents(entry.amountCents)} Alloy tokens for ${entry.label}` : undefined}
          className={`flex ${unit.tokens ? "h-[52px] flex-col gap-0.5" : "h-11"} items-center justify-center rounded-xl border border-sm-ink/15 bg-white text-[15px] font-bold tabular-nums text-sm-ink transition hover:border-sm-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent disabled:cursor-not-allowed disabled:opacity-50 data-[state=on]:border-sm-primary data-[state=on]:bg-sm-primary/[0.06] data-[state=on]:text-sm-primary data-[state=on]:shadow-[inset_0_0_0_1px_#003B8D]`}
        >
          {unit.tokens ? (
            <>
              <span className="leading-none">{unit.fromCents(entry.amountCents)}</span>
              <span className="text-[11px] font-semibold leading-none opacity-70">tokens · {entry.label}</span>
            </>
          ) : entry.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}

export function TrustLine({ className = "", unit = DOLLARS }) {
  return (
    <p className={`flex items-center justify-center gap-2 text-xs text-sm-ink/65 ${className}`}>
      <ShieldCheck size={14} className="text-[#16A34A]" aria-hidden="true" />
      Paid securely on Stripe. {unit.tokens ? "Tokens" : "Credits"} apply within a minute.
    </p>
  );
}

/** Last 30 days on Alloy: what routing saved, the model mix, and the work behind it. */
export function RoutingPanel({ routing }) {
  return (
    <Panel data-testid="alloy-routing" aria-labelledby="alloy-routing-title">
      <PanelTitle id="alloy-routing-title" aside={<span className="text-xs text-sm-ink/65">Last 30 days</span>}>Auto routing</PanelTitle>
      {routing.state === "unavailable" ? (
        <p className="mt-3 text-[13px] text-sm-ink/65">Routing details aren't available right now. Check back in a few minutes.</p>
      ) : routing.state === "empty" ? (
        <p className="mt-3 text-[13px] leading-relaxed text-sm-ink/70">Your savings show up here after your agent's first full day on Alloy.</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <div data-testid="alloy-savings" className="font-display text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-sm-ink">{routing.headline}</div>
            {routing.comparison ? <div className="text-sm text-sm-ink/70">{routing.comparison}</div> : null}
          </div>
          {routing.method ? <p className="mt-1.5 text-[13px] text-sm-ink/65">{routing.method}</p> : null}
          {routing.hasMix ? (
            <ShareBar
              testId="alloy-routing-mix"
              parts={routing.mix.map((entry) => ({ key: entry.tier, percent: entry.percent, barClass: TIER_STYLES[entry.tier].bar, legend: `${entry.percent}% ${TIER_STYLES[entry.tier].label}` }))}
              note={routing.note}
            />
          ) : null}
          <StatGrid stats={routing.stats} className="mt-4 border-t border-sm-ink/10 pt-4" />
        </>
      )}
    </Panel>
  );
}

export function UsagePanel({ usage, onRefresh, refreshing, unit = DOLLARS }) {
  const stats = [
    { label: "Today", value: usage.dailyUsd },
    { label: "This week", value: usage.weeklyUsd },
    { label: "This month", value: usage.monthlyUsd },
  ];
  return (
    <Panel data-testid="alloy-usage" aria-labelledby="alloy-usage-title">
      <PanelTitle id="alloy-usage-title" aside={
        <button type="button" onClick={onRefresh} disabled={refreshing} aria-label="Refresh" className="inline-flex min-h-[32px] items-center gap-1.5 rounded-lg px-1.5 text-xs font-medium text-sm-ink/65 hover:text-sm-ink disabled:opacity-50">
          <RefreshCw size={13} aria-hidden="true" className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      }>Usage</PanelTitle>
      {/* On tokens the unit sits under each number: "1,250 tokens" in one line collides across the three columns on a tablet. */}
      <StatGrid stats={stats.map((stat) => ({ label: stat.label, value: unit.amount(stat.value), ...(unit.tokens ? { sub: "tokens" } : {}) }))} className="mt-3" />
      {/* "Paid for with": an agent on its own image account makes images these numbers rightly leave out. */}
      <p className="mt-3 text-[13px] text-sm-ink/65">Includes images paid for with {unit.name}.</p>
    </Panel>
  );
}

/** Uses completed daily activity, which has a different window from the live Usage totals. */
export function ModelUsagePanel({ routing, configured, keyReady, unit = DOLLARS }) {
  return (
    <Panel data-testid="alloy-model-usage" aria-labelledby="alloy-model-usage-title">
      <PanelTitle id="alloy-model-usage-title">Usage by model</PanelTitle>
      <ModelUsageBody routing={routing} configured={configured} keyReady={keyReady} unit={unit} />
    </Panel>
  );
}

/** The model table and its states, without the panel: the By model tab of the usage-by-person panel shows the same. */
export function ModelUsageBody({ routing, configured, keyReady, unit = DOLLARS }) {
  const models = Array.isArray(routing?.models) ? routing.models : null;
  const empty = configured === false || !keyReady || (models && models.length === 0);
  const used = (usd) => (unit.tokens ? unit.recorded(usd) : usd > 0 && usd < 0.01 ? "<$0.01" : formatUsd(usd));
  return (
    <>
      <p className="mt-1 text-xs leading-relaxed text-sm-ink/65">Last 30 completed days (UTC) · Updated daily. Share is the percentage of requests.</p>
      {empty ? (
        <p className="mt-3 text-[13px] leading-relaxed text-sm-ink/70">Your model breakdown appears after your agent's first full day of usage on Alloy.</p>
      ) : !models ? (
        <p className="mt-3 text-[13px] text-sm-ink/65">Model usage isn't available right now. Try refreshing in a few minutes.</p>
      ) : (
        <table className="mt-4 w-full table-fixed text-left text-xs tabular-nums">
          <caption className="sr-only">Model usage for the last 30 completed UTC days. {unit.tokens ? "Tokens used are billed Alloy tokens; model tokens are the text the model read and wrote." : "Credits used are billed Alloy credits."}</caption>
          <thead className="text-sm-ink/60">
            <tr>
              <th scope="col" className="w-[40%] pb-2 pr-3 font-medium">Model</th>
              <th scope="col" className="w-[20%] pb-2 text-right font-medium">Requests</th>
              <th scope="col" className="w-[17%] pb-2 text-right font-medium">Share</th>
              <th scope="col" className="w-[23%] pb-2 pl-2 text-right font-medium">{unit.tokens ? "Tokens used" : "Credits used"}</th>
            </tr>
          </thead>
          <tbody>
            {models.map((row) => (
              <tr key={row.model} className="border-t border-sm-ink/[0.08]">
                <th scope="row" className="py-3 pr-3 align-top font-normal">
                  <span className="block break-words text-[13px] font-bold text-sm-ink [overflow-wrap:anywhere]">{row.model === "unknown" ? "Unknown model" : row.model}</span>
                  <span className="mt-1 block text-[11px] text-sm-ink/60">{formatCount(row.tokens)} model tokens</span>
                  <span aria-hidden="true" className="mt-2 block h-1.5 overflow-hidden rounded-full bg-sm-primary/[0.08]">
                    <span className="block h-full rounded-full bg-sm-primary" style={{ width: `${routing.requests > 0 ? Math.min(100, (row.requests / routing.requests) * 100) : 0}%` }} />
                  </span>
                </th>
                <td className="py-3 text-right align-top text-sm-ink">{Number(row.requests).toLocaleString()}</td>
                <td className="py-3 text-right align-top font-bold text-sm-primary">{routing.requests === 0 ? "—" : row.requestPercent === 0 && row.requests > 0 ? "<1%" : `${row.requestPercent}%`}</td>
                <td className="py-3 pl-2 text-right align-top text-sm-ink">{used(row.usageUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

const PURCHASE_ICONS = Object.freeze({ auto_recharge: RefreshCw, migration: Sparkles, adjustment: Sparkles, reward: Trophy });

const TOP_UPS_SHOWN = 6;

export function TopUpsPanel({ purchases, unit = DOLLARS }) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? purchases : purchases.slice(0, TOP_UPS_SHOWN);
  return (
    <Panel data-testid="alloy-topups" aria-labelledby="alloy-topups-title">
      <PanelTitle id="alloy-topups-title" aside={purchases.length > TOP_UPS_SHOWN ? (
        <button type="button" data-testid="credits-history-toggle" onClick={() => setShowAll((value) => !value)} className="min-h-[32px] rounded-lg px-1.5 text-xs font-bold text-sm-primary hover:text-sm-ink">
          {showAll ? "Show recent" : `Show all ${purchases.length}`}
        </button>
      ) : null}>Recent top-ups</PanelTitle>
      <ul data-testid="credits-history" className="mt-2">
        {purchases.length === 0 ? (
          <li className="py-3 text-[13px] text-sm-ink/60">No top-ups yet.</li>
        ) : shown.map((row) => {
          const Icon = PURCHASE_ICONS[row.kind] || Receipt;
          const applying = row.status === "pending_apply";
          return (
            <li key={row.id} className="grid min-h-[52px] grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3.5 border-t border-sm-ink/[0.08] py-2 first:border-t-0">
              <IconSquare icon={Icon} />
              <div className="min-w-0">
                <div className="truncate text-sm font-bold text-sm-ink">{purchaseLabel(row.kind)}</div>
                <div className="text-xs tabular-nums text-sm-ink/65">{new Date(row.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-sm font-bold tabular-nums text-sm-ink">+{unit.fromCents(row.amountCents)}{unit.tokens ? " tokens" : ""}</span>
                <span className={`inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-bold ${applying ? "border-sm-accent/40 bg-sm-accent/10 text-sm-primary" : "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#16A34A]"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${applying ? "bg-sm-accent" : "bg-[#22C55E]"}`} aria-hidden="true" />
                  {applying ? "Applying" : "Settled"}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

export function RoleNote({ canManage }) {
  return (
    <p className="flex items-center gap-2 px-1 text-xs text-sm-ink/60">
      <UsersRound size={14} aria-hidden="true" />
      {canManage ? "Owners and admins can change these settings." : "Ask a workspace Owner or Admin to change these settings."}
    </p>
  );
}

/** A billing follower's right column: its agent's usage, savings, top-ups and model are its host brand's. */
export function SharedAlloyPanel({ hostName }) {
  return (
    <Panel data-testid="alloy-shared-panel" aria-labelledby="alloy-shared-title" className="flex flex-col gap-2">
      <PanelTitle id="alloy-shared-title">{`Runs on ${hostName}'s Alloy`}</PanelTitle>
      <p className="text-sm text-sm-ink/75">
        {`This brand's agent uses ${hostName}'s Alloy key and tokens. Usage, savings, top-ups, the model and reasoning effort are on ${hostName}'s Alloy page; ask an owner or admin of ${hostName} to change them.`}
      </p>
    </Panel>
  );
}

/** A brand profile that shares its host brand's billing: its Alloy runs on the host's credits. */
export function SharedBillingNote({ hostName }) {
  return (
    <p data-testid="shared-billing-note" className="flex items-center gap-2 px-1 text-xs text-sm-ink/60">
      <UsersRound size={14} aria-hidden="true" />
      {`Billing shared with ${hostName}`}
    </p>
  );
}

export function Notice({ tone = "info", children, testId = "alloy-notice" }) {
  const styles = tone === "error"
    ? "border-[#EF4343]/25 bg-[#EF4343]/[0.06] text-red-800"
    : tone === "success"
      ? "border-[#22C55E]/30 bg-[#22C55E]/[0.08] text-[#166534]"
      : "border-sm-accent/30 bg-sm-accent/[0.08] text-sm-ink";
  return (
    <div role={tone === "error" ? "alert" : "status"} data-testid={testId} className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm ${styles}`}>
      <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}

/** Flipping Alloy on without credits: pick the starting credits, pay on Stripe, and Alloy turns on after. */
export function TurnOnDialog({ open, onOpenChange, packages, selectedCents, onSelect, onContinue, busy, unit = DOLLARS }) {
  const selected = packages.find((entry) => entry.amountCents === selectedCents) || packages[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="alloy-turn-on-dialog" className="max-w-md rounded-2xl border-sm-ink/10 bg-white p-6 text-sm-ink">
        <DialogTitle className="font-display text-xl font-bold tracking-tight">Turn on GrowthOS Alloy</DialogTitle>
        <DialogDescription className="text-sm leading-relaxed text-sm-ink/70">
          Pick your starting {unit.noun}. Your agent switches to Alloy as soon as your payment clears.
        </DialogDescription>
        <AmountChoices packages={packages} selectedCents={selected?.amountCents} onSelect={onSelect} disabled={busy} unit={unit} />
        <button
          type="button"
          data-testid="alloy-turn-on-pay"
          disabled={busy || !selected}
          onClick={() => selected && onContinue(selected.amountCents)}
          className="flex h-[46px] w-full items-center justify-center rounded-xl bg-sm-primary text-[15px] font-bold text-white shadow-[0_8px_18px_-10px_rgba(0,59,141,0.75)] transition hover:bg-sm-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {selected ? `Continue to payment · ${packageChoiceLabel(selected, unit)}` : "Continue to payment"}
        </button>
        <TrustLine unit={unit} />
      </DialogContent>
    </Dialog>
  );
}
